import { useMemo, useState } from "react";
import { ProductRanking, ProductSortKey } from "./types";
import { usd } from "./helpers";

interface TopProductsCardProps {
  products: ProductRanking[];
  limit?: number;
}

export const TopProductsCard = ({ products, limit = 25 }: TopProductsCardProps) => {
  const [sortBy, setSortBy] = useState<ProductSortKey>("units");

  const ranked = useMemo(() => {
    const sorted = [...products].sort((a, b) =>
      sortBy === "units" ? b.unitsSold - a.unitsSold : b.gross - a.gross
    );
    return sorted.slice(0, limit);
  }, [products, sortBy, limit]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900">Top Products</h2>
        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-sm">
          <SortButton
            active={sortBy === "units"}
            onClick={() => setSortBy("units")}
          >
            Units Sold
          </SortButton>
          <SortButton
            active={sortBy === "gross"}
            onClick={() => setSortBy("gross")}
          >
            Gross Sales
          </SortButton>
        </div>
      </div>

      {ranked.length === 0 ? (
        <div className="text-sm text-gray-500 py-6 text-center">
          No product sales for this event.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 uppercase tracking-wide border-b border-gray-200">
              <th className="text-left py-2 font-medium w-12">#</th>
              <th className="text-left py-2 font-medium">Product</th>
              <th className="text-left py-2 font-medium">Vendor</th>
              <th className="text-right py-2 font-medium">Units</th>
              <th className="text-right py-2 font-medium">Gross</th>
              <th className="text-right py-2 font-medium">Avg Price</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {ranked.map((p, i) => (
              <tr key={`${p.vendorName}::${p.posName}`} className="text-gray-700">
                <td className="py-2 text-gray-400 tabular-nums">{i + 1}</td>
                <td className="py-2 font-medium text-gray-900">{p.posName}</td>
                <td className="py-2 text-gray-500">{p.vendorName}</td>
                <td className="py-2 text-right tabular-nums">{p.unitsSold}</td>
                <td className="py-2 text-right tabular-nums text-green-700 font-medium">
                  {usd(p.gross)}
                </td>
                <td className="py-2 text-right tabular-nums">{usd(p.avgPrice)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const SortButton = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 transition-colors ${
      active
        ? "bg-red-700 text-white font-semibold"
        : "bg-white text-gray-600 hover:bg-gray-50"
    }`}
  >
    {children}
  </button>
);
